/**
 * Admin console — Staff list + create + edit.
 *
 * Two surfaces side-by-side:
 *
 *   1. LIST  — `GET /api/v2/mail/admin/accounts` rendered as a table.
 *              Click a row → opens the inline editor below the row.
 *              Edit: display_name (the From: header name) + suspend /
 *              unsuspend with a required reason. Audit rows captured
 *              on the daemon side per CLAUDE.md mail conventions.
 *
 *   2. CREATE — `POST /api/v2/mail/admin/staff/provision` end-to-end
 *              orchestration (Zitadel + Stalwart + DB). Initial
 *              password shown ONCE; admin shares it OOB. Same shape
 *              as before.
 *
 * Why a single route, not /admin/staff/list + /admin/staff/new?
 * The two operations share the same actor capability (send_as) and
 * the same mental model (manage the staff roster). Splitting routes
 * forces an admin to nav back-and-forth when they create someone +
 * realize the display name was wrong. One screen is denser but
 * faster for the common case.
 *
 * What we deliberately DON'T edit here (yet):
 *  - local_part / email address — touches Stalwart principal name +
 *    Zitadel userName + DNS-bound external references. Needs its
 *    own dedicated flow with rollback.
 *  - role (super_admin / admin / staff) — driven by Zitadel claims
 *    today (env-var allow-lists in capabilities.ts). Lift into a
 *    DB-backed grant when the third staff member needs a custom
 *    capability bundle.
 *
 * Capability: send_as (admin / super_admin) for every action.
 */

import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type AdminAccountListItem } from '../lib/api';
import { AdminShell } from '../components/AdminShell';

export const Route = createFileRoute('/admin')({
  component: AdminConsoleRoute,
});

function AdminConsoleRoute() {
  return (
    <AdminShell mode="feature" featureName="Staff">
      <AdminConsole />
    </AdminShell>
  );
}

function AdminConsole() {
  // ── List state ────────────────────────────────────────────────
  const [items, setItems] = useState<AdminAccountListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((n) => n + 1), []);

  // Debounce the search input so each keystroke doesn't hammer the
  // endpoint. 300ms is the standard "search-as-you-type" feel.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setListLoading(true);
      setListError(null);
      try {
        const res = await api.listAccounts({
          q: debouncedSearch || undefined,
          limit: 50,
        });
        if (!cancelled) setItems(res.items);
      } catch (err) {
        if (!cancelled) {
          setListError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, refreshKey]);

  const editingItem = useMemo(
    () => items.find((it) => it.id === editingId) ?? null,
    [items, editingId],
  );

  return (
    <div
      className="px-4 md:px-6 py-6 md:py-8"
      style={{ maxWidth: 880, margin: '0 auto', width: '100%' }}
    >
      <h1
        className="text-2xl md:text-3xl"
        style={{
          fontFamily: '"Source Serif 4", Georgia, serif',
          fontWeight: 600,
          margin: 0,
        }}
      >
        Staff
      </h1>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          color: 'var(--c-fg-secondary)',
          marginTop: 6,
        }}
      >
        Provision new staff and edit existing accounts. Display name
        flows into the From: header on outbound mail; suspending an
        account disables IMAP, SMTP, and the admin send-as path.
      </p>

      {/* ── Create form ──────────────────────────────────────────
          Create on top so the most common new-user flow doesn't
          force scrolling past the existing-staff list every time.
          The list is the longer surface (grows linearly with the
          team) but is a less-frequent action. */}
      <section style={{ marginTop: 28 }}>
        <h2
          style={{
            fontFamily: '"Source Serif 4", Georgia, serif',
            fontWeight: 600,
            fontSize: 18,
            margin: 0,
            marginBottom: 12,
          }}
        >
          Create staff
        </h2>
        <CreateStaffForm onCreated={refresh} />
      </section>

      {/* ── Staff list ───────────────────────────────────────── */}
      <section style={{ marginTop: 36 }}>
        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between"
          style={{ gap: 8, marginBottom: 12 }}
        >
          <h2
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontWeight: 600,
              fontSize: 18,
              margin: 0,
            }}
          >
            Existing staff
          </h2>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid var(--c-border-hairline)',
              background: 'var(--c-bg-surface)',
              color: 'var(--c-fg-default)',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              minWidth: 240,
              outline: 'none',
            }}
          />
        </div>

        {listError ? (
          <p
            role="alert"
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--c-fg-danger, #C7563E)',
            }}
          >
            {listError}
          </p>
        ) : null}

        <div
          style={{
            border: '1px solid var(--c-border-hairline)',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--c-bg-surface)',
          }}
        >
          {listLoading && items.length === 0 ? (
            <div style={emptyStyle}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={emptyStyle}>
              {debouncedSearch
                ? `No accounts match "${debouncedSearch}".`
                : 'No accounts yet.'}
            </div>
          ) : (
            items.map((it, idx) => (
              <StaffRow
                key={it.id}
                item={it}
                editing={editingId === it.id}
                onToggleEdit={() =>
                  setEditingId((cur) => (cur === it.id ? null : it.id))
                }
                onMutated={() => {
                  setEditingId(null);
                  refresh();
                }}
                isLast={idx === items.length - 1}
              />
            ))
          )}
        </div>

        {editingItem ? null : null}
      </section>
    </div>
  );
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 16px',
  fontSize: 13,
  color: 'var(--c-fg-tertiary)',
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif',
};

// ────────────────────────────────────────────────────────────────
// Per-row: shows the account, expands to inline editor on click.
// Edit panel handles display-name PATCH + suspend/unsuspend.
// ────────────────────────────────────────────────────────────────

function StaffRow({
  item,
  editing,
  onToggleEdit,
  onMutated,
  isLast,
}: {
  item: AdminAccountListItem;
  editing: boolean;
  onToggleEdit: () => void;
  onMutated: () => void;
  isLast: boolean;
}) {
  return (
    <div
      style={{
        borderBottom: isLast ? 'none' : '1px solid var(--c-border-hairline)',
      }}
    >
      <button
        type="button"
        onClick={onToggleEdit}
        style={{
          width: '100%',
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: 'var(--c-fg-default)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.display_name ?? item.local_part}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--c-fg-secondary)',
              fontFamily: 'ui-monospace, monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.primary_email}
          </div>
        </div>
        <StatusPill status={item.status} />
        <span
          aria-hidden
          style={{
            fontSize: 14,
            color: 'var(--c-fg-tertiary)',
            transform: editing ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 120ms ease',
            display: 'inline-block',
          }}
        >
          ›
        </span>
      </button>
      {editing ? <EditPanel item={item} onMutated={onMutated} /> : null}
    </div>
  );
}

function StatusPill({ status }: { status: AdminAccountListItem['status'] }) {
  const isSuspended = status === 'suspended';
  const isDeleted = status === 'deleted';
  return (
    <span
      style={{
        fontSize: 11,
        padding: '2px 8px',
        borderRadius: 999,
        background: isDeleted
          ? 'var(--c-bg-surface-alt)'
          : isSuspended
            ? 'rgba(199, 86, 62, 0.12)'
            : 'rgba(108, 142, 80, 0.14)',
        color: isDeleted
          ? 'var(--c-fg-tertiary)'
          : isSuspended
            ? 'var(--c-fg-danger, #C7563E)'
            : 'var(--c-fg-success, #5C7A3D)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
// Inline editor — display_name + suspend toggle. Two distinct save
// buttons so an admin doesn't accidentally suspend someone while
// fixing a typo in their name.
// ────────────────────────────────────────────────────────────────

function EditPanel({
  item,
  onMutated,
}: {
  item: AdminAccountListItem;
  onMutated: () => void;
}) {
  const [displayName, setDisplayName] = useState(item.display_name ?? '');
  const [busy, setBusy] = useState<'name' | 'suspend' | 'unsuspend' | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [suspendReason, setSuspendReason] = useState('');

  // Reset transient UI whenever the row's underlying data changes.
  useEffect(() => {
    setDisplayName(item.display_name ?? '');
    setError(null);
    setStatus(null);
    setSuspendReason('');
  }, [item.id, item.display_name, item.status]);

  const dirtyName = displayName !== (item.display_name ?? '');

  async function saveName() {
    if (!dirtyName || busy) return;
    setBusy('name');
    setError(null);
    setStatus(null);
    try {
      const res = await api.updateAccount({
        id: item.id,
        displayName: displayName.trim() || null,
      });
      if (res.ok) {
        setStatus('Saved.');
        onMutated();
      } else {
        setError(`Failed: ${res.reason}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(null);
    }
  }

  async function doSuspend() {
    const reason = suspendReason.trim();
    if (!reason) {
      setError('Reason is required to suspend.');
      return;
    }
    if (busy) return;
    setBusy('suspend');
    setError(null);
    setStatus(null);
    try {
      const res = await api.suspendAccount({ id: item.id, reason });
      if (res.ok) {
        setStatus('Suspended.');
        onMutated();
      } else {
        setError(`Failed: ${res.reason}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(null);
    }
  }

  async function doUnsuspend() {
    if (busy) return;
    setBusy('unsuspend');
    setError(null);
    setStatus(null);
    try {
      const res = await api.unsuspendAccount({ id: item.id });
      if (res.ok) {
        setStatus('Re-enabled.');
        onMutated();
      } else if (res.reason === 'not_suspended') {
        setStatus('Already active.');
      } else {
        setError(`Failed: ${res.reason}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        padding: '14px 16px 18px',
        background: 'var(--c-bg-surface-alt)',
        borderTop: '1px solid var(--c-border-hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* Display name editor */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--c-fg-secondary)',
          }}
        >
          Display name (From: header)
        </label>
        <div
          className="flex flex-col md:flex-row md:items-center"
          style={{ gap: 8 }}
        >
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={item.local_part}
            disabled={busy === 'name'}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid var(--c-border-hairline)',
              background: 'var(--c-bg-surface)',
              color: 'var(--c-fg-default)',
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={saveName}
            disabled={!dirtyName || !!busy}
            style={{
              ...buttonStyle({ disabled: !dirtyName || !!busy }),
              alignSelf: 'flex-start',
            }}
          >
            {busy === 'name' ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </div>

      {/* Suspend / unsuspend */}
      <div
        style={{
          paddingTop: 12,
          borderTop: '1px dashed var(--c-border-hairline)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--c-fg-secondary)',
          }}
        >
          Account state
        </div>
        {item.status === 'suspended' ? (
          <div
            className="flex flex-col md:flex-row md:items-center"
            style={{ gap: 8 }}
          >
            <span
              style={{
                fontSize: 13,
                color: 'var(--c-fg-secondary)',
                flex: 1,
              }}
            >
              Suspended
              {item.suspended_reason ? (
                <>
                  {' — '}
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>
                    {item.suspended_reason}
                  </span>
                </>
              ) : null}
            </span>
            <button
              type="button"
              onClick={doUnsuspend}
              disabled={!!busy}
              style={buttonStyle({ disabled: !!busy, variant: 'primary' })}
            >
              {busy === 'unsuspend' ? 'Re-enabling…' : 'Re-enable'}
            </button>
          </div>
        ) : (
          <div
            className="flex flex-col md:flex-row md:items-center"
            style={{ gap: 8 }}
          >
            <input
              type="text"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason (required, e.g. departed_employee)"
              disabled={!!busy}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid var(--c-border-hairline)',
                background: 'var(--c-bg-surface)',
                color: 'var(--c-fg-default)',
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={doSuspend}
              disabled={!suspendReason.trim() || !!busy}
              style={buttonStyle({
                disabled: !suspendReason.trim() || !!busy,
                variant: 'danger',
              })}
            >
              {busy === 'suspend' ? 'Suspending…' : 'Suspend'}
            </button>
          </div>
        )}
      </div>

      {error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            fontSize: 12,
            color: 'var(--c-fg-danger, #C7563E)',
          }}
        >
          {error}
        </p>
      ) : null}
      {status ? (
        <p
          role="status"
          style={{ margin: 0, fontSize: 12, color: 'var(--c-fg-secondary)' }}
        >
          {status}
        </p>
      ) : null}

      {/* App passwords — list + revoke + create-lockout toggle */}
      <AppPasswordsBlock item={item} onMutated={onMutated} />

      {/* Metadata footer — small, read-only */}
      <div
        style={{
          paddingTop: 10,
          borderTop: '1px dashed var(--c-border-hairline)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 11,
          color: 'var(--c-fg-tertiary)',
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        <span>Account id: {item.id}</span>
        {item.candidate_id ? (
          <span>Zitadel sub: {item.candidate_id}</span>
        ) : null}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// App-passwords block — list this user's IMAP/SMTP credentials and
// expose per-row Revoke + a top-of-block "Allow creating new client
// passwords" toggle. The toggle stores `app_passwords_disabled` on
// the account row; create-side path returns 'disabled_by_admin' when
// the flag is true.
//
// Revoke caveat (called out in the row UI): until task #48 lands,
// Stalwart still carries the plaintext secret even after revoke. For
// immediate hard cut-off, admins should use Suspend in the section
// above. Surfacing the caveat avoids surprise.
// ────────────────────────────────────────────────────────────────

type AccountAppPassword = {
  id: string;
  label: string;
  scope: 'imap' | 'smtp' | 'imap+smtp';
  created_at: string;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  locked_until: string | null;
};

function AppPasswordsBlock({
  item,
  onMutated,
}: {
  item: AdminAccountListItem;
  onMutated: () => void;
}) {
  const [passwords, setPasswords] = useState<AccountAppPassword[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [revokeBusyId, setRevokeBusyId] = useState<string | null>(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Local mirror of the lockout flag for optimistic UI; reset on item
  // change so a parent refresh stays authoritative.
  const [disabled, setDisabled] = useState(item.app_passwords_disabled);
  useEffect(() => {
    setDisabled(item.app_passwords_disabled);
  }, [item.id, item.app_passwords_disabled]);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    api
      .listAccountAppPasswords({ id: item.id })
      .then((res) => {
        if (!cancelled) setPasswords(res.items);
      })
      .catch((err) => {
        if (!cancelled) {
          setListError(err instanceof Error ? err.message : 'Failed to load');
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, refreshKey]);

  async function doToggle(nextEnabled: boolean) {
    if (toggleBusy) return;
    setToggleBusy(true);
    setStatus(null);
    try {
      const res = await api.setAccountAppPasswordsEnabled({
        id: item.id,
        enabled: nextEnabled,
      });
      if (res.ok) {
        setDisabled(res.disabled);
        setStatus(nextEnabled ? 'New client passwords allowed.' : 'Locked — no new client passwords.');
        onMutated();
      } else {
        setStatus(`Failed: ${res.reason}`);
      }
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setToggleBusy(false);
    }
  }

  async function doRevoke(pwId: string) {
    if (revokeBusyId) return;
    setRevokeBusyId(pwId);
    setStatus(null);
    try {
      const res = await api.revokeAccountAppPassword({
        id: item.id,
        appPasswordId: pwId,
      });
      if (res.ok) {
        setStatus('Revoked.');
        setRefreshKey((n) => n + 1);
      } else {
        setStatus(`Failed: ${res.reason}`);
      }
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setRevokeBusyId(null);
    }
  }

  const active = (passwords ?? []).filter((p) => !p.revoked_at);
  const revoked = (passwords ?? []).filter((p) => p.revoked_at);

  return (
    <div
      style={{
        paddingTop: 12,
        borderTop: '1px dashed var(--c-border-hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--c-fg-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span>External-client app passwords</span>
        <button
          type="button"
          onClick={() => doToggle(disabled)}
          disabled={toggleBusy}
          style={{
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 999,
            border: '1px solid var(--c-border-hairline)',
            background: disabled
              ? 'rgba(199, 86, 62, 0.12)'
              : 'var(--c-bg-surface)',
            color: disabled
              ? 'var(--c-fg-danger, #C7563E)'
              : 'var(--c-fg-default)',
            cursor: toggleBusy ? 'wait' : 'pointer',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
          title={
            disabled
              ? 'User cannot mint new client passwords. Click to allow.'
              : 'User can mint new client passwords. Click to lock.'
          }
        >
          {toggleBusy
            ? 'Saving…'
            : disabled
              ? 'Creation locked'
              : 'Creation allowed'}
        </button>
      </div>

      {listError ? (
        <p
          role="alert"
          style={{
            margin: 0,
            fontSize: 12,
            color: 'var(--c-fg-danger, #C7563E)',
          }}
        >
          {listError}
        </p>
      ) : null}

      {listLoading && passwords === null ? (
        <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
          Loading…
        </span>
      ) : active.length === 0 && revoked.length === 0 ? (
        <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
          No client passwords on this account.
        </span>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--c-border-hairline)',
            borderRadius: 6,
            overflow: 'hidden',
            background: 'var(--c-bg-surface)',
          }}
        >
          {active.map((pw, idx) => (
            <AppPasswordRow
              key={pw.id}
              pw={pw}
              busy={revokeBusyId === pw.id}
              onRevoke={() => doRevoke(pw.id)}
              isLast={idx === active.length - 1 && revoked.length === 0}
            />
          ))}
          {revoked.map((pw, idx) => (
            <AppPasswordRow
              key={pw.id}
              pw={pw}
              busy={false}
              onRevoke={null}
              isLast={idx === revoked.length - 1}
            />
          ))}
        </div>
      )}

      {status ? (
        <p
          role="status"
          style={{ margin: 0, fontSize: 12, color: 'var(--c-fg-secondary)' }}
        >
          {status}
        </p>
      ) : null}
    </div>
  );
}

function AppPasswordRow({
  pw,
  busy,
  onRevoke,
  isLast,
}: {
  pw: AccountAppPassword;
  busy: boolean;
  onRevoke: (() => void) | null;
  isLast: boolean;
}) {
  const isRevoked = !!pw.revoked_at;
  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: isLast ? 'none' : '1px solid var(--c-border-hairline)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        opacity: isRevoked ? 0.55 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--c-fg-default)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {pw.label}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--c-fg-tertiary)',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {pw.scope} · created {formatDate(pw.created_at)}
          {pw.last_used_at
            ? ` · last used ${formatDate(pw.last_used_at)}${pw.last_used_ip ? ` (${pw.last_used_ip})` : ''}`
            : ' · never used'}
          {isRevoked
            ? ` · revoked ${formatDate(pw.revoked_at!)}${pw.revoked_reason ? ` (${pw.revoked_reason})` : ''}`
            : null}
        </div>
      </div>
      {onRevoke ? (
        <button
          type="button"
          onClick={onRevoke}
          disabled={busy}
          style={{
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 4,
            border: '1px solid var(--c-fg-danger, #C7563E)',
            background: 'transparent',
            color: 'var(--c-fg-danger, #C7563E)',
            cursor: busy ? 'wait' : 'pointer',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Revoking…' : 'Revoke'}
        </button>
      ) : null}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = parseServerDate(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function buttonStyle({
  disabled,
  variant = 'primary',
}: {
  disabled: boolean;
  variant?: 'primary' | 'danger';
}): React.CSSProperties {
  const isDanger = variant === 'danger';
  return {
    padding: '8px 16px',
    borderRadius: 999,
    border: isDanger ? '1px solid var(--c-fg-danger, #C7563E)' : 'none',
    background: isDanger ? 'transparent' : 'var(--c-brand, #B8523A)',
    color: isDanger
      ? 'var(--c-fg-danger, #C7563E)'
      : 'var(--c-fg-on-accent, #FAF7F2)',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };
}

// ────────────────────────────────────────────────────────────────
// Create form — preserved from the previous AdminConsole. Calls
// /admin/staff/provision; renders the one-time initial password on
// success. Invokes onCreated() so the list above re-fetches.
// ────────────────────────────────────────────────────────────────

type CreatedStaff = {
  zitadel_sub: string;
  account_id: string;
  platform_email: string;
  initial_password: string;
};

function CreateStaffForm({ onCreated }: { onCreated: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailLocalPart, setEmailLocalPart] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedStaff | null>(null);
  const [copied, setCopied] = useState(false);

  const ready = firstName.trim().length > 0 && lastName.trim().length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    setCreated(null);
    try {
      const res = await api.provisionStaff({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailLocalPart: emailLocalPart.trim() || undefined,
        displayName: displayName.trim() || undefined,
      });
      if (res.success) {
        setCreated({
          zitadel_sub: res.zitadel_sub,
          account_id: res.account_id,
          platform_email: res.platform_email,
          initial_password: res.initial_password,
        });
        setFirstName('');
        setLastName('');
        setEmailLocalPart('');
        setDisplayName('');
        onCreated();
      } else {
        setError(res.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(false);
    }
  }

  async function copyPassword() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.initial_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (http context); inline display is the
      // fallback — admin can select-all + copy manually.
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          color: 'var(--c-fg-tertiary)',
          margin: 0,
        }}
      >
        Provisions the Zitadel user, Stalwart mailbox, and database row
        in one step. The initial password is shown ONCE on success and
        not stored — share it with the staff member out-of-band.
      </p>

      <div className="flex flex-col md:flex-row" style={{ gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field
            label="First name"
            value={firstName}
            onChange={setFirstName}
            required
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field
            label="Last name"
            value={lastName}
            onChange={setLastName}
            required
          />
        </div>
      </div>
      <Field
        label="Email local part (optional)"
        hint='Default: "firstname.lastname". Override to choose a custom address.'
        value={emailLocalPart}
        onChange={setEmailLocalPart}
        autoCapitalize="off"
        autoComplete="off"
        suffix="@postopia.com"
      />
      <Field
        label="Display name (optional)"
        hint='Default: "First Last". Used in the From: header on outbound mail.'
        value={displayName}
        onChange={setDisplayName}
      />

      {error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            fontSize: 12,
            color: 'var(--c-fg-danger, #C7563E)',
          }}
        >
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!ready || busy}
        className="self-stretch md:self-start"
        style={{
          padding: '12px 20px',
          borderRadius: 999,
          border: 'none',
          background: 'var(--c-brand, #B8523A)',
          color: 'var(--c-fg-on-accent, #FAF7F2)',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: 14,
          cursor: !ready || busy ? 'not-allowed' : 'pointer',
          opacity: !ready || busy ? 0.6 : 1,
          marginTop: 4,
        }}
      >
        {busy ? 'Provisioning…' : 'Create staff member'}
      </button>

      {created ? (
        <div
          role="status"
          style={{
            marginTop: 12,
            padding: 16,
            borderRadius: 8,
            border: '1px solid var(--c-border-hairline)',
            background: 'var(--c-bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <h3
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontWeight: 600,
              fontSize: 16,
              margin: 0,
            }}
          >
            Staff member created
          </h3>
          <Row label="Email" value={created.platform_email} mono />
          <Row label="Zitadel id" value={created.zitadel_sub} mono />
          <Row label="Account id" value={created.account_id} mono />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: '10px 12px',
              borderRadius: 6,
              background: 'var(--c-bg-surface-alt)',
              border: '1px solid var(--c-border-hairline)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--c-fg-secondary)',
                  fontWeight: 500,
                }}
              >
                Initial password (shown once)
              </span>
              <button
                type="button"
                onClick={copyPassword}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 4,
                  border: '1px solid var(--c-border-hairline)',
                  background: 'transparent',
                  color: 'var(--c-fg-default)',
                  cursor: 'pointer',
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <code
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: 14,
                wordBreak: 'break-all',
                color: 'var(--c-fg-default)',
              }}
            >
              {created.initial_password}
            </code>
            <span style={{ fontSize: 11, color: 'var(--c-fg-tertiary)' }}>
              The user will be prompted to change it on first sign-in.
            </span>
          </div>
        </div>
      ) : null}
    </form>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  required,
  autoCapitalize,
  autoComplete,
  suffix,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoCapitalize?: string;
  autoComplete?: string;
  suffix?: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 500 }}>
        {label}
        {required ? (
          <span style={{ color: 'var(--c-brand, #B8523A)' }}> *</span>
        ) : null}
      </span>
      {hint ? (
        <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
          {hint}
        </span>
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: 8,
          border: '1px solid var(--c-border-hairline)',
          background: 'var(--c-bg-surface)',
          marginTop: 2,
        }}
      >
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoCapitalize={autoCapitalize}
          autoComplete={autoComplete}
          required={required}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: 'none',
            background: 'transparent',
            color: 'var(--c-fg-default)',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            outline: 'none',
          }}
        />
        {suffix ? (
          <span
            style={{
              alignSelf: 'center',
              padding: '0 12px',
              fontSize: 13,
              color: 'var(--c-fg-tertiary)',
              borderLeft: '1px solid var(--c-border-hairline)',
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            {suffix}
          </span>
        ) : null}
      </div>
    </label>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      className="flex-col md:flex-row md:items-baseline md:gap-2"
      style={{ display: 'flex', gap: 2 }}
    >
      <span
        className="md:min-w-[100px]"
        style={{
          fontSize: 12,
          color: 'var(--c-fg-secondary)',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: mono ? 'ui-monospace, monospace' : 'Inter, sans-serif',
          fontSize: 13,
          color: 'var(--c-fg-default)',
          wordBreak: 'break-all',
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </div>
  );
}
